import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Grid, 
  Tabs, 
  Tab, 
  Paper,
  Typography,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  SelectChangeEvent 
} from '@mui/material';
import { 
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon 
} from '@mui/icons-material';
import { WorkflowManager } from '../WorkflowManager/WorkflowManager';
import { PaymentRequestQueue } from './PaymentRequestQueue';
import { ReconciliationDashboard } from './ReconciliationDashboard';
import { ComplianceMonitor } from './ComplianceMonitor';
import { StateRegistrationTracker } from './StateRegistrationTracker';
import { ReportingSchedule } from './ReportingSchedule';
import { FundDashboard } from './FundDashboard';
import { TransactionDetailsDrawer } from './drawers/TransactionDetailsDrawer';
import { useTransactionService } from '../../hooks/useTransactionService';
import { FundManagementService } from '../../services/FundManagementService';
import { stateRequirements, federalRequirements, multiStateRequirements } from '../../../data/stateRequirements';
import { StateRequirement, FederalRequirement } from '../../types/compliance';
import { ReconciliationTransaction } from '../../types/accounting';
import { ReportingScheduleProps } from './ReportingSchedule';
import { ReconciliationDashboardProps } from './ReconciliationDashboard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          bgcolor: '#0A1929',
          height: '100%'
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const FundManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFund, setSelectedFund] = useState<string | 'all'>('all');
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCreatingTransaction, setIsCreatingTransaction] = useState(false);
  const [funds, setFunds] = useState<Array<{ id: string; name: string }>>([]);
  const [stateReqs, setStateReqs] = useState<StateRequirement[]>([]);
  const [federalReqs, setFederalReqs] = useState<FederalRequirement[]>([]);

  const { getTransactions } = useTransactionService();
  const fundService = new FundManagementService();

  useEffect(() => {
    const loadFunds = async () => {
      try {
        const fundsData = await fundService.getFunds();
        setFunds(fundsData);
        if (fundsData.length > 0) {
          setSelectedFund(fundsData[0].id);
        }
      } catch (error) {
        console.error('Failed to load funds:', error);
      }
    };
    loadFunds();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleTransactionClick = useCallback((transaction: ReconciliationTransaction) => {
    // Handle transaction click
    console.log('Transaction clicked:', transaction);
  }, []);

  const handleCreateTransaction = () => {
    setIsCreatingTransaction(true);
  };

  const handleFundChange = (event: SelectChangeEvent<string>) => {
    setSelectedFund(event.target.value);
  };

  const handleScheduleUpdate = useCallback(async (updates: any): Promise<void> => {
    try {
      // Handle schedule updates
      console.log('Schedule updated:', updates);
    } catch (error) {
      console.error('Error updating schedule:', error);
    }
  }, []);

  return (
    <Box sx={{ 
      flexGrow: 1, 
      color: 'text.primary', 
      bgcolor: '#0A1929',
      minHeight: '100vh',
      p: 0
    }}>
      {/* Header with Fund Selector and Action Button */}
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
      }}>
        <Box>
          <Typography variant="h4" sx={{ color: 'text.primary', mb: 0.5 }}>
            Fund Operations
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            Comprehensive fund operations, compliance, and workflow management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl 
            sx={{ 
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2962FF',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiSelect-icon': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
          >
            <InputLabel id="fund-select-label">Select Fund</InputLabel>
            <Select
              labelId="fund-select-label"
              id="fund-select"
              value={selectedFund}
              label="Select Fund"
              onChange={handleFundChange}
            >
              <MenuItem 
                value="all"
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'rgba(82,153,255,0.08)',
                  },
                }}
              >
                All Funds
              </MenuItem>
              {funds.map((fund) => (
                <MenuItem 
                  key={fund.id} 
                  value={fund.id}
                  sx={{
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'rgba(82,153,255,0.08)',
                    },
                  }}
                >
                  {fund.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTransaction}
            sx={{ 
              background: 'linear-gradient(90deg, #2962FF 0%, #34D399 100%)',
              '&:hover': {
                background: 'linear-gradient(90deg, #1E4BD8 0%, #2AAC7D 100%)',
              }
            }}
          >
            New Transaction
          </Button>
        </Box>
      </Box>

      {/* Streamlined Navigation */}
      <Box sx={{ 
        bgcolor: '#0A1929',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
      }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
              height: '2px'
            },
            '& .MuiTab-root': {
              minHeight: '44px',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.875rem',
              textTransform: 'none',
              fontWeight: 500,
              '&.Mui-selected': {
                color: 'primary.main'
              },
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.9)'
              }
            }
          }}
        >
          <Tab label="Payment Requests" />
          <Tab label="Reconciliation" />
          <Tab label="Compliance" />
          <Tab label="State Registrations" />
          <Tab label="Reporting Schedule" />
          <Tab label="Workflow" />
        </Tabs>
      </Box>

      {/* Content Area */}
      <Box sx={{ 
        bgcolor: '#0A1929',
        p: 0,
        height: 'calc(100vh - 144px)', // Account for header and tabs
        overflow: 'auto'
      }}>
        <TabPanel value={activeTab} index={0}>
          <PaymentRequestQueue 
            fundId={selectedFund}
            onApprove={async (requestId) => {
              // Handle approve
            }}
            onReject={async (requestId, reason) => {
              // Handle reject
            }}
            onRequestInfo={async (requestId, info) => {
              // Handle request info
            }}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <ReconciliationDashboard 
            fundId={selectedFund}
            onTransactionClick={handleTransactionClick}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <ComplianceMonitor 
            fundId={selectedFund}
            stateRequirements={stateReqs}
            federalRequirements={federalReqs}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          <StateRegistrationTracker />
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          <ReportingSchedule 
            fundId={selectedFund}
            onScheduleUpdate={handleScheduleUpdate}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={5}>
          <WorkflowManager />
        </TabPanel>
      </Box>
    </Box>
  );
}; 